import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Share2, Twitter, Linkedin } from 'lucide-react';
import { auth, googleProvider, signInWithPopup } from '../firebase';
import { BLOG_DATA } from '../lib/blogData';
import { YachtLogo } from '../components/YachtLogo';

import Navbar from '../components/Navbar';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = slug ? BLOG_DATA[slug] : null;

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Yacht Labs Blog`;
      // In a real app, you'd also update meta tags here for SEO
    }
  }, [post]);

  const handleGetStarted = async () => {
    if (!auth.currentUser) {
      try {
        await signInWithPopup(auth, googleProvider);
        navigate('/studio');
      } catch (error) {
        console.error("Error signing in:", error);
      }
    } else {
      navigate('/studio');
    }
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center flex-col">
        <h1 className="text-4xl font-mono uppercase tracking-tighter mb-4">Post Not Found</h1>
        <Link to="/" className="text-[#0074D9] hover:underline font-mono uppercase tracking-widest text-sm">
          Return Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <main className="pt-32 pb-24 px-6">
        <article className="max-w-3xl mx-auto">
          {/* Back Link */}
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition-colors mb-12">
            <ArrowLeft size={14} /> Back to Home
          </Link>

          {/* Post Header */}
          <header className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-zinc-900 border border-zinc-800 rounded-full text-[10px] font-mono uppercase tracking-widest text-zinc-300">
                {post.tag}
              </span>
              <span className="text-xs font-mono text-zinc-500">{post.date} · {post.readTime}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-mono uppercase tracking-tighter mb-8 leading-tight">
              {post.title}
            </h1>
            
            {/* Share Buttons */}
            <div className="flex items-center gap-4 border-y border-zinc-900 py-4 mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">Share:</span>
              <button className="text-zinc-400 hover:text-[#1DA1F2] transition-colors"><Twitter size={18} /></button>
              <button className="text-zinc-400 hover:text-[#0A66C2] transition-colors"><Linkedin size={18} /></button>
              <button className="text-zinc-400 hover:text-white transition-colors"><Share2 size={18} /></button>
            </div>
          </header>

          {/* Featured Image */}
          <div className="mb-16 rounded-2xl overflow-hidden border border-zinc-900">
            <img src={post.image} alt={post.title} className="w-full h-auto object-cover" referrerPolicy="no-referrer" />
          </div>

          {/* Content */}
          <div 
            className="prose prose-invert prose-zinc max-w-none prose-headings:font-mono prose-headings:uppercase prose-headings:tracking-tight prose-p:text-zinc-300 prose-p:font-light prose-p:leading-relaxed prose-a:text-[#0074D9] prose-blockquote:border-[#0074D9] prose-blockquote:bg-[#0074D9]/5 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-li:text-zinc-300 prose-li:font-light"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Bottom CTA */}
          <div className="mt-24 p-10 bg-zinc-900/30 border border-zinc-800 rounded-3xl text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,116,217,0.1),transparent_50%)]" />
            <div className="relative z-10">
              <h3 className="text-2xl font-mono uppercase tracking-tighter mb-4">Ready to build like this?</h3>
              <p className="text-zinc-400 font-light mb-8 max-w-md mx-auto">
                I used Yacht Labs to do this. If you want to go from idea → product in hours instead of weeks, you can try it here.
              </p>
              <button 
                onClick={handleGetStarted}
                className="px-8 py-4 bg-white text-black rounded-xl font-mono uppercase text-sm tracking-widest font-bold hover:bg-zinc-200 hover:scale-[1.02] transition-all inline-flex items-center gap-2"
              >
                Start Shipping <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </article>
      </main>
    </div>
  );
}
